import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'src/lib/routing';

import { useFileUploadDownload } from 'containers/Studies/Results/Unmoderated/components/Task/FileUpload/useFileUploadDownload';

import type { DownloadOutcome } from 'containers/Studies/Results/Unmoderated/components/Task/FileUpload/useFileUploadDownload';

/**
 * Landing page for the file links in exports: `/uploads/:taskResultId?user_type=client`.
 * `RequireAuth` reads `user_type` to send a signed-out visitor to the correct login. The page
 * then requests a fresh signed URL for the task result and downloads it. Thus the exported
 * link works while the reader has the team, and no presigned URL goes into the export.
 */
export const UploadRedirect = () => {
  const { t } = useTranslation();
  const { taskResultId } = useParams();
  const { download } = useFileUploadDownload();
  // Held here rather than read from the mutation, so the page shows the spinner from the first
  // paint. The mutation is still idle then, and an idle state reads the same as a finished one.
  const [outcome, setOutcome] = useState<DownloadOutcome | null>(null);

  useEffect(() => {
    if (taskResultId) {
      void download(taskResultId).then(setOutcome);
    }
  }, [taskResultId, download]);

  const content = () => {
    if (outcome === 'failed') {
      return <div className="text-destructive">{t('sections.studies.results.fileUpload.downloadFailed')}</div>;
    }

    if (outcome === 'started') {
      return <div>{t('sections.studies.results.fileUpload.downloadStarted')}</div>;
    }

    return (
      <>
        <Loader2 className="size-6 animate-spin" aria-hidden="true" />
        <div>{t('sections.studies.results.fileUpload.preparingDownload')}</div>
      </>
    );
  };

  return (
    <div
      aria-live="polite"
      className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground"
    >
      {content()}
    </div>
  );
};
